// The one responsive mechanism (plan T6): web/src had zero @media and zero
// matchMedia, the sidebar was an inline `width: wide ? 224 : 60`, and at 390px
// that left 166px of content (SH-01, XA-07).
//
// Breakpoints are JS constants because a custom property cannot be read inside
// @media; any @media in tokens.css uses these literals with a comment pointing
// here. Values: Hyprflow_Design_Spec.md §4.6, "breakpoints at 1024 / 768 / 480".
//
// Each value is where its band ENDS — the width is below it:
//   sm  < 480        phone
//   md  480 – 767    large phone; "below md" in the plan is sm + md
//   lg  768 – 1023   tablet; the icon rail
//   xl  ≥ 1024       desktop; the wide sidebar
import { useCallback, useSyncExternalStore } from 'react';

export const BREAKPOINTS = { sm: 480, md: 768, lg: 1024 } as const;

export type Breakpoint = 'sm' | 'md' | 'lg' | 'xl';

const mql = (query: string): MediaQueryList | null =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(query)
    : null;

/**
 * Whether `query` matches now, re-rendering when it changes.
 *
 * `fallback` is the answer where there is no media engine — server render, or
 * jsdom, which has no matchMedia. It defaults to false, the CSS reading of a
 * query that cannot be evaluated.
 */
export function useMediaQuery(query: string, fallback = false): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const list = mql(query);
      list?.addEventListener('change', onChange);
      return () => list?.removeEventListener('change', onChange);
    },
    [query],
  );
  return useSyncExternalStore(
    subscribe,
    () => mql(query)?.matches ?? fallback,
    () => fallback,
  );
}

/**
 * The current band. With no media engine it is 'xl': the app is desktop-first,
 * and every existing jsdom test renders the desktop layout, so a missing
 * matchMedia must not quietly switch them all to the phone one.
 */
export function useBreakpoint(): Breakpoint {
  const lg = useMediaQuery(`(min-width: ${BREAKPOINTS.lg}px)`, true);
  const md = useMediaQuery(`(min-width: ${BREAKPOINTS.md}px)`, true);
  const sm = useMediaQuery(`(min-width: ${BREAKPOINTS.sm}px)`, true);
  return lg ? 'xl' : md ? 'lg' : sm ? 'md' : 'sm';
}

/**
 * Whether the width is below `BREAKPOINTS[edge]`: `useBelow('md')` is a phone
 * (no sidebar, the bottom action bar), `useBelow('sm')` a small one (dialogs
 * become bottom sheets). One query rather than three. No media engine is
 * desktop, as for useBreakpoint.
 */
export const useBelow = (edge: keyof typeof BREAKPOINTS): boolean =>
  !useMediaQuery(`(min-width: ${BREAKPOINTS[edge]}px)`, true);
